import type { MaybeRefOrGetter } from "vue";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { computed, toValue } from "vue";
import { useAuthStore } from "../AuthStore/useAuthStore";
import { PermissionService } from "../Permissions/PermissionService";

/**
 * Whether the permissions of `service` read a client's installs: the global `app_subscription.read_any` or a
 * client-scoped `app_subscription.read` on that client (see canReadAppCollectionViaClient). Kept apart from the
 * App Store access module, which loads the shared permission service, so the resource catalog can use it too.
 */
export function readsInstallsOfClient(clientIri: string | null | undefined, service: PermissionService): boolean {
  if (service.getGlobalPermissionForUser({
    attribute: PermissionAttributesEnum.GlobalOperationAppsubscriptionReadany,
  })) {
    return true;
  }

  if (!clientIri) {
    return false;
  }

  return service.getClientPermissionForUser({
    clientIri,
    attribute: PermissionAttributesEnum.ContextClientOperationAppsubscriptionRead,
  }) !== null;
}

/**
 * {@link readsInstallsOfClient} over the permissions of the person signed in right now, for the client the getter
 * names, reactive to both. The shared permission service keeps a copy of the permissions that is refreshed on its
 * own `init`, which runs after a new identity's other reads already started (impersonation, account switch), so a
 * gate on requests that must follow the identity at once (the background install reads) asks a service over the
 * auth store's current permissions instead.
 */
export function useCanReadInstallsOfClient(clientIri: MaybeRefOrGetter<string | null | undefined>) {
  const authStore = useAuthStore();

  return computed<boolean>(() => {
    const permissions = authStore.user.value?.permissions ?? [];
    const current = new PermissionService({
      setUserPermissions: () => permissions,
      setUserIri: () => authStore.user.value?.["@id"] ?? null,
      setMode: () => "client",
      setDefaultLocation: () => null,
      setDefaultClient: () => null,
      setLocationClientMap: () => new Map(),
      setUsersData: () => new Map(),
    });
    return readsInstallsOfClient(toValue(clientIri), current);
  });
}
